import { Controller } from '@hotwired/stimulus';
import { csrfHeaders } from '../services/form.services';

export class QuickActionsController extends Controller {
    declare readonly triggerTarget: HTMLElement
    declare readonly actionTargets: HTMLElement[]
    declare readonly voiceOverlayTarget: HTMLElement
    declare readonly voiceStatusTarget: HTMLElement
    declare readonly voiceTranscriptTarget: HTMLElement
    declare readonly voiceEditLinkTarget: HTMLAnchorElement
    declare readonly helpInputTarget: HTMLTextAreaElement
    declare readonly helpSubmitTarget: HTMLButtonElement
    declare readonly helpConversationTarget: HTMLElement
    declare readonly helpStatusTarget: HTMLElement
    declare readonly helpCloseTarget: HTMLButtonElement
    declare readonly hasTriggerTarget: boolean
    declare readonly hasVoiceOverlayTarget: boolean
    declare readonly hasVoiceStatusTarget: boolean
    declare readonly hasVoiceTranscriptTarget: boolean
    declare readonly hasVoiceEditLinkTarget: boolean
    declare readonly hasHelpInputTarget: boolean
    declare readonly hasHelpSubmitTarget: boolean
    declare readonly hasHelpConversationTarget: boolean
    declare readonly hasHelpStatusTarget: boolean
    declare readonly hasHelpCloseTarget: boolean

    static targets = ['trigger', 'action', 'voiceOverlay', 'voiceStatus', 'voiceTranscript', 'voiceEditLink', 'helpInput', 'helpSubmit', 'helpConversation', 'helpStatus', 'helpClose']

    declare openValue: boolean
    static values = {
        open: Boolean
    }

    private boundHandleOutsideClick!: (event: Event) => void
    private boundHandleEscape!: (event: KeyboardEvent) => void
    private readonly boundReset = () => this.reset()
    private longPressTimer: number | null = null
    private longPressStarted = false
    private longPressPointerActive = false
    private stopWhenRecordingStarts = false
    private mediaRecorder: MediaRecorder | null = null
    private audioStream: MediaStream | null = null
    private audioChunks: Blob[] = []
    private isRecording = false
    private isProcessingVoiceMeal = false
    private voiceCaptureSubmitted = false
    private activeVoiceMealRequestId: string | null = null
    private voiceOverlayHideTimer: number | null = null
    private voiceOverlayAnimationTimer: number | null = null
    private quickActionsClosingTimer: number | null = null
    private helpHistory: Array<{ role: 'user' | 'assistant'; content: string; links?: Array<{ label: string; href: string }> }> = []
    private helpPending = false
    private helpTypingGeneration = 0

    connect(): void {
        this.boundHandleOutsideClick = this.handleOutsideClick.bind(this)
        this.boundHandleEscape = this.handleEscape.bind(this)

        document.addEventListener('click', this.boundHandleOutsideClick)
        document.addEventListener('keydown', this.boundHandleEscape)
        document.addEventListener('turbo:before-cache', this.boundReset)
        document.addEventListener('turbo:before-render', this.boundReset)
        document.addEventListener('turbo:render', this.boundReset)
        document.addEventListener('turbo:load', this.boundReset)
        window.addEventListener('ovasai:quick-actions:reset', this.boundReset)
        this.updateUi()
    }

    disconnect(): void {
        this.reset()
        this.stopVoiceCapture()
        this.clearSelectedState()
        document.removeEventListener('click', this.boundHandleOutsideClick)
        document.removeEventListener('keydown', this.boundHandleEscape)
        document.removeEventListener('turbo:before-cache', this.boundReset)
        document.removeEventListener('turbo:before-render', this.boundReset)
        document.removeEventListener('turbo:render', this.boundReset)
        document.removeEventListener('turbo:load', this.boundReset)
        window.removeEventListener('ovasai:quick-actions:reset', this.boundReset)
    }

    toggle(event: Event): void {
        event.preventDefault()
        if (!this.hasTriggerTarget) {
            return
        }
        if (this.triggerTarget.dataset.quickActionsSuppressClick === 'true') {
            delete this.triggerTarget.dataset.quickActionsSuppressClick
            return
        }
        this.closeHelp()
        this.openValue = !this.openValue
    }

    beginLongPress = (event: PointerEvent): void => {
        if (!this.hasTriggerTarget) {
            return
        }
        if (event.pointerType === 'mouse' && event.button !== 0) {
            return
        }

        this.longPressStarted = false
        this.longPressPointerActive = true
        this.stopWhenRecordingStarts = false
        this.clearLongPressTimer()
        this.longPressTimer = window.setTimeout(() => {
            this.longPressStarted = true
            this.triggerTarget.dataset.quickActionsSuppressClick = 'true'
            void this.startVoiceMealCapture()
        }, 520)
    }

    endLongPress = (): void => {
        if (!this.hasTriggerTarget) {
            return
        }
        this.longPressPointerActive = false
        this.clearLongPressTimer()
        if (!this.longPressStarted) {
            return
        }

        this.triggerTarget.dataset.quickActionsSuppressClick = 'true'
        if (this.isRecording) {
            this.mediaRecorder?.stop()
        } else {
            this.stopWhenRecordingStarts = true
        }
    }

    cancelLongPress = (): void => {
        this.longPressPointerActive = false
        this.clearLongPressTimer()
        if (this.longPressStarted && this.isRecording) {
            this.mediaRecorder?.stop()
        } else if (this.longPressStarted) {
            this.stopWhenRecordingStarts = true
        }
    }

    suppressContextMenu = (event: Event): void => {
        if (this.longPressStarted || this.isRecording || this.isProcessingVoiceMeal) {
            event.preventDefault()
        }
    }

    close(): void {
        this.openValue = false
    }

    open(): void {
        this.openValue = true
    }

    openHelp(event?: Event): void {
        event?.preventDefault()
        this.openValue = false
        this.clearHelpConversation()
        this.element.classList.add('quick-actions-help-open')
        window.requestAnimationFrame(() => {
            this.helpInputTarget?.focus()
        })
    }

    followAction(event: Event): void {
        event.preventDefault()
        event.stopPropagation()

        const action = event.currentTarget
        if (!(action instanceof HTMLAnchorElement)) {
            this.close()
            return
        }

        action.classList.add('quick-action-item-selected')
        this.close()

        window.setTimeout(() => {
            this.clearSelectedState()
            this.visit(action.href)
        }, 620)
    }

    openNewMeal(event: Event): void {
        event.preventDefault()
        if (event.target instanceof Element && event.target.closest('a')) {
            return
        }
        const card = event.currentTarget as HTMLElement | null
        const url = card?.dataset.quickActionsUrl
        if (!card || !url) {
            return
        }
        card.classList.add('quick-action-item-selected')
        this.close()
        window.setTimeout(() => {
            this.clearSelectedState()
            this.visit(url)
        }, 320)
    }

    async askHelp(event: SubmitEvent): Promise<void> {
        event.preventDefault()
        event.stopPropagation()
        if (!this.hasHelpInputTarget || this.helpPending) {
            return
        }

        const message = this.helpInputTarget.value.trim()
        if (!message) {
            this.helpInputTarget.focus()
            return
        }

        if (this.helpHistory.length > 0) {
            this.helpTypingGeneration += 1
            this.helpHistory = []
            this.helpConversationTarget?.replaceChildren()
        }
        this.helpPending = true
        this.setHelpLoading(true)
        this.showHelpStatus('Ovasai is checking...')
        this.helpHistory.push({ role: 'user', content: message })
        this.helpInputTarget.value = ''
        this.resizeHelpInput()
        this.renderHelpConversation()
        const chatGeneration = this.helpTypingGeneration
        try {
            const response = await fetch('/app-help', {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    ...csrfHeaders(),
                },
                body: JSON.stringify({
                    message,
                    history: [],
                    current_page: document.body.dataset.featurePage || null,
                }),
            })
            const data = await response.json().catch(() => null)
            if (!response.ok || typeof data?.answer !== 'string') {
                throw new Error(data?.detail || data?.message || 'Ovasai Help is unavailable right now.')
            }
            if (chatGeneration !== this.helpTypingGeneration) {
                return
            }

            const answer = this.safeHelpAnswer(data.answer)
            const links = this.safeHelpLinks(data.links)
            this.helpHistory.push({ role: 'assistant', content: answer, links })
            this.hideHelpStatus()
            await this.typeHelpAnswer(answer, links)
        } catch (error) {
            this.showHelpStatus(error instanceof Error ? error.message : 'Ovasai Help is unavailable right now.', true)
        } finally {
            this.helpPending = false
            this.setHelpLoading(false)
        }
    }

    resizeHelpInput(): void {
        if (!this.hasHelpInputTarget) {
            return
        }
        this.helpInputTarget.style.height = 'auto'
        this.helpInputTarget.style.height = `${Math.min(this.helpInputTarget.scrollHeight, 128)}px`
    }

    handleHelpKeydown(event: KeyboardEvent): void {
        if (event.key !== 'Enter' || event.shiftKey || event.isComposing) {
            return
        }
        event.preventDefault()
        this.helpInputTarget.form?.requestSubmit()
    }

    closeHelpConversation(): void {
        this.clearHelpConversation()
        this.closeHelp()
    }

    private clearHelpConversation(): void {
        this.helpTypingGeneration += 1
        this.helpHistory = []
        if (this.hasHelpConversationTarget) {
            this.helpConversationTarget.replaceChildren()
            this.helpConversationTarget.classList.add('hidden')
            this.helpConversationTarget.setAttribute('aria-live', 'polite')
        }
        if (this.hasHelpCloseTarget) {
            this.helpCloseTarget.classList.add('hidden')
        }
        this.hideHelpStatus()
    }

    private renderHelpConversation(): void {
        if (!this.hasHelpConversationTarget) {
            return
        }
        this.helpConversationTarget.replaceChildren()
        for (const message of this.helpHistory) {
            this.helpConversationTarget.append(this.createHelpMessage(message))
        }
        this.helpConversationTarget.classList.remove('hidden')
        if (this.hasHelpCloseTarget) {
            this.helpCloseTarget.classList.remove('hidden')
        }
        this.helpConversationTarget.scrollTop = this.helpConversationTarget.scrollHeight
    }

    private async typeHelpAnswer(answer: string, links: Array<{ label: string; href: string }>): Promise<void> {
        if (!this.hasHelpConversationTarget) {
            return
        }

        const bubble = document.createElement('div')
        bubble.className = 'quick-actions-help-message quick-actions-help-message-assistant'
        bubble.setAttribute('aria-label', answer)
        this.helpConversationTarget.append(bubble)
        this.helpConversationTarget.classList.remove('hidden')
        if (this.hasHelpCloseTarget) {
            this.helpCloseTarget.classList.remove('hidden')
        }

        const generation = ++this.helpTypingGeneration
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        if (reduceMotion) {
            bubble.textContent = answer
            this.appendHelpLinks(bubble, links)
            this.helpConversationTarget.scrollTop = this.helpConversationTarget.scrollHeight
            return
        }

        this.helpConversationTarget.setAttribute('aria-live', 'off')
        for (let index = 0; index < answer.length; index += 1) {
            if (generation !== this.helpTypingGeneration || !this.element.isConnected) {
                this.helpConversationTarget.setAttribute('aria-live', 'polite')
                return
            }
            bubble.textContent = answer.slice(0, index + 1)
            this.helpConversationTarget.scrollTop = this.helpConversationTarget.scrollHeight
            await new Promise<void>(resolve => window.setTimeout(resolve, 14))
        }
        this.appendHelpLinks(bubble, links)
        this.helpConversationTarget.setAttribute('aria-live', 'polite')
    }

    private createHelpMessage(message: { role: 'user' | 'assistant'; content: string; links?: Array<{ label: string; href: string }> }): HTMLElement {
        const bubble = document.createElement('div')
        bubble.className = message.role === 'user'
            ? 'quick-actions-help-message quick-actions-help-message-user'
            : 'quick-actions-help-message quick-actions-help-message-assistant'
        const copy = document.createElement('p')
        copy.textContent = message.content
        bubble.append(copy)
        this.appendHelpLinks(bubble, message.links || [])
        return bubble
    }

    private appendHelpLinks(container: HTMLElement, links: Array<{ label: string; href: string }>): void {
        if (links.length === 0) {
            return
        }
        const actions = document.createElement('div')
        actions.className = 'quick-actions-help-links'
        for (const link of links) {
            const anchor = document.createElement('a')
            anchor.href = link.href
            anchor.textContent = link.label
            anchor.dataset.turboFrame = '_top'
            actions.append(anchor)
        }
        container.append(actions)
    }

    private safeHelpLinks(value: unknown): Array<{ label: string; href: string }> {
        if (!Array.isArray(value)) {
            return []
        }
        return value.flatMap(item => {
            if (!item || typeof item !== 'object') {
                return []
            }
            const candidate = item as { label?: unknown; href?: unknown }
            if (typeof candidate.label !== 'string' || typeof candidate.href !== 'string' || !candidate.href.startsWith('/')) {
                return []
            }
            return [{ label: candidate.label, href: candidate.href }]
        }).slice(0, 3)
    }

    private safeHelpAnswer(value: string): string {
        return value
            .replace(/\[([^\]]+)\]\((?:https?:\/\/|www\.|\/)[^)]+\)/gi, '$1')
            .replace(/\s*\((?:https?:\/\/|www\.|\/)[^)]+\)/gi, '')
            .replace(/(?:https?:\/\/|www\.)\S+/gi, '')
            .replace(/(^|\s)\/(?:[A-Za-z0-9_-]+\/?)+(?:\?[^\s,.;!?)]*)?/g, '$1')
            .replace(/[ \t]{2,}/g, ' ')
            .replace(/ +([,.;!?])/g, '$1')
            .trim()
    }

    private showHelpStatus(message: string, isError = false): void {
        if (!this.hasHelpStatusTarget) {
            return
        }
        this.helpStatusTarget.textContent = message
        this.helpStatusTarget.classList.toggle('quick-actions-help-status-error', isError)
        this.helpStatusTarget.classList.remove('hidden')
    }

    private hideHelpStatus(): void {
        if (!this.hasHelpStatusTarget) {
            return
        }
        this.helpStatusTarget.classList.add('hidden')
    }

    private setHelpLoading(loading: boolean): void {
        if (this.hasHelpInputTarget) {
            this.helpInputTarget.disabled = loading
        }
        if (this.hasHelpSubmitTarget) {
            this.helpSubmitTarget.disabled = loading
            this.helpSubmitTarget.innerHTML = loading
                ? '<i class="fas fa-spinner fa-spin" aria-hidden="true"></i>'
                : '<i class="fas fa-arrow-up" aria-hidden="true"></i>'
        }
    }

    openValueChanged(): void {
        this.updateUi()
    }

    reset(): void {
        this.openValue = false
        this.clearLongPressTimer()
        this.longPressStarted = false
        this.longPressPointerActive = false
        this.stopWhenRecordingStarts = false
        this.clearSelectedState()
        this.hideVoiceOverlay()
        this.updateUi()
        this.clearQuickActionsClosingTimer()
        this.element.classList.remove('quick-actions-open', 'quick-actions-closing', 'quick-actions-help-open')
        this.resetActionDelays()
    }

    private updateUi(): void {
        this.clearQuickActionsClosingTimer()
        if (this.openValue) {
            this.element.classList.remove('quick-actions-closing')
            this.applyStaggeredDelays()
            this.element.classList.add('quick-actions-open')
        } else {
            const wasOpen = this.element.classList.contains('quick-actions-open')
            this.element.classList.remove('quick-actions-open')
            if (wasOpen) {
                this.element.classList.add('quick-actions-closing')
                this.applyExitDelays()
                this.quickActionsClosingTimer = window.setTimeout(() => {
                    this.element.classList.remove('quick-actions-closing')
                    this.resetActionDelays()
                    this.quickActionsClosingTimer = null
                }, 680)
            } else {
                this.element.classList.remove('quick-actions-closing')
                this.resetActionDelays()
            }
        }
        if (this.hasTriggerTarget) {
            this.triggerTarget.setAttribute('aria-expanded', String(this.openValue))
        }
    }

    private applyStaggeredDelays(): void {
        this.actionTargets.forEach((action, index) => {
            action.style.transitionDelay = `${35 + index * 60}ms`
        })
    }

    private applyExitDelays(): void {
        const lastIndex = this.actionTargets.length - 1
        this.actionTargets.forEach((action, index) => {
            action.style.transitionDelay = `${(lastIndex - index) * 55}ms`
        })
    }

    private resetActionDelays(): void {
        for (const action of this.actionTargets) {
            action.style.transitionDelay = '0ms'
        }
    }

    private clearQuickActionsClosingTimer(): void {
        if (this.quickActionsClosingTimer !== null) {
            window.clearTimeout(this.quickActionsClosingTimer)
            this.quickActionsClosingTimer = null
        }
    }

    private visit(url: string): void {
        const turbo = window.Turbo as { visit?: (location: string) => void } | undefined
        if (typeof turbo?.visit === 'function') {
            turbo.visit(url)
            return
        }

        window.location.assign(url)
    }

    private async startVoiceMealCapture(): Promise<void> {
        if (!this.hasTriggerTarget || this.isRecording || this.isProcessingVoiceMeal) {
            return
        }

        if (!this.isSpeechToTextSupported()) {
            this.showVoiceOverlay('Mic unavailable in this browser.', '', 4000)
            return
        }

        try {
            this.close()
            this.audioChunks = []
            this.voiceCaptureSubmitted = false
            this.activeVoiceMealRequestId = this.createVoiceMealRequestId()
            this.showVoiceOverlay('Listening... keep holding the plus button.', '')
            this.audioStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
            this.mediaRecorder = new MediaRecorder(this.audioStream)

            this.mediaRecorder.addEventListener('dataavailable', (mediaEvent: BlobEvent) => {
                if (mediaEvent.data.size > 0) {
                    this.audioChunks.push(mediaEvent.data)
                }
            })

            this.mediaRecorder.addEventListener('stop', async () => {
                if (this.voiceCaptureSubmitted) {
                    return
                }
                this.voiceCaptureSubmitted = true
                const mimeType = this.mediaRecorder?.mimeType || this.audioChunks[0]?.type || 'audio/webm'
                const audioBlob = new Blob(this.audioChunks, { type: mimeType })
                this.stopVoiceCapture()
                await this.submitVoiceMeal(audioBlob)
            })

            this.mediaRecorder.start()
            this.isRecording = true
            if (this.hasTriggerTarget) {
                this.triggerTarget.classList.add('quick-actions-voice-recording')
            }
            if (this.stopWhenRecordingStarts || !this.longPressPointerActive) {
                this.mediaRecorder.stop()
            }
        } catch {
            this.stopVoiceCapture()
            this.showVoiceOverlay('Microphone blocked. Allow mic access to use voice meal logging.', '', 4000)
        }
    }

    private async submitVoiceMeal(audioBlob: Blob): Promise<void> {
        if (this.isProcessingVoiceMeal) {
            return
        }

        if (audioBlob.size === 0) {
            this.showVoiceOverlay('No speech captured. Hold the plus button and describe your meal.', '', 4000)
            return
        }

        this.isProcessingVoiceMeal = true
        const voiceRequestId = this.activeVoiceMealRequestId || this.createVoiceMealRequestId()
        this.activeVoiceMealRequestId = voiceRequestId
        if (this.hasTriggerTarget) {
            this.triggerTarget.classList.add('quick-actions-voice-processing')
        }
        this.showVoiceOverlay('Analyzing and logging your meal...', '')

        try {
            const extension = this.mimeTypeToFileExtension(audioBlob.type)
            const audioFile = new File([audioBlob], `voice-meal.${extension}`, { type: audioBlob.type || 'audio/webm' })
            const formData = new FormData()
            formData.append('audio', audioFile)
            formData.append('voice_request_id', voiceRequestId)

            const response = await fetch('/analyse/voice-meal', {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    ...csrfHeaders(),
                },
                body: formData,
            })

            const data = await response.json().catch(() => null)
            if (!response.ok || !data?.success || typeof data.redirect_url !== 'string') {
                throw new Error(data?.message || 'Voice meal logging failed.')
            }

            const mealSummary = typeof data.meal_summary === 'string' ? data.meal_summary.trim() : ''
            const mealName = typeof data.meal_name === 'string' ? data.meal_name.trim() : ''
            const loggedLabel = mealSummary || mealName || 'your meal'
            const editUrl = typeof data.edit_url === 'string' ? data.edit_url : undefined
            this.showVoiceOverlay(
                `Logged ${loggedLabel}.`,
                'Tap Edit if you want to change the meal, items, time, or details.',
                10000,
                editUrl,
            )
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Voice meal logging failed.'
            this.showVoiceOverlay(message, '', 4000)
        } finally {
            this.isProcessingVoiceMeal = false
            this.activeVoiceMealRequestId = null
            if (this.hasTriggerTarget) {
                this.triggerTarget.classList.remove('quick-actions-voice-processing')
            }
        }
    }

    private stopVoiceCapture(): void {
        if (this.mediaRecorder) {
            this.mediaRecorder.ondataavailable = null
            this.mediaRecorder.onstop = null
            this.mediaRecorder = null
        }

        if (this.audioStream) {
            this.audioStream.getTracks().forEach(track => track.stop())
            this.audioStream = null
        }

        this.isRecording = false
        this.audioChunks = []
        this.stopWhenRecordingStarts = false
        if (this.hasTriggerTarget) {
            this.triggerTarget.classList.remove('quick-actions-voice-recording')
        }
    }

    private showVoiceOverlay(status: string, transcript: string, autoHideAfterMs?: number, editUrl?: string): void {
        if (!this.hasVoiceOverlayTarget || !this.hasVoiceStatusTarget || !this.hasVoiceTranscriptTarget) {
            return
        }

        this.clearVoiceOverlayHideTimer()
        this.clearVoiceOverlayAnimationTimer()
        this.voiceStatusTarget.textContent = status
        this.voiceTranscriptTarget.textContent = transcript
        this.voiceTranscriptTarget.classList.toggle('hidden', !transcript)
        this.updateVoiceEditLink(editUrl)
        this.voiceOverlayTarget.classList.remove('hidden')
        this.voiceOverlayTarget.classList.remove('quick-actions-voice-overlay-hiding')
        void this.voiceOverlayTarget.offsetWidth
        this.voiceOverlayTarget.classList.add('quick-actions-voice-overlay-visible')
        this.voiceOverlayTarget.setAttribute('aria-hidden', 'false')
        if (autoHideAfterMs && autoHideAfterMs > 0) {
            this.voiceOverlayHideTimer = window.setTimeout(() => {
                this.hideVoiceOverlay()
            }, autoHideAfterMs)
        }
    }

    private hideVoiceOverlay(): void {
        if (!this.hasVoiceOverlayTarget) {
            return
        }

        this.clearVoiceOverlayHideTimer()
        this.clearVoiceOverlayAnimationTimer()
        this.voiceOverlayTarget.classList.remove('quick-actions-voice-overlay-visible')
        this.voiceOverlayTarget.classList.add('quick-actions-voice-overlay-hiding')
        this.voiceOverlayTarget.setAttribute('aria-hidden', 'true')
        this.voiceOverlayAnimationTimer = window.setTimeout(() => {
            if (!this.hasVoiceOverlayTarget || !this.hasVoiceTranscriptTarget) {
                this.voiceOverlayAnimationTimer = null
                return
            }
            this.voiceOverlayTarget.classList.add('hidden')
            this.voiceOverlayTarget.classList.remove('quick-actions-voice-overlay-hiding')
            this.voiceTranscriptTarget.textContent = ''
            this.voiceTranscriptTarget.classList.add('hidden')
            this.updateVoiceEditLink()
            this.voiceOverlayAnimationTimer = null
        }, 220)
    }

    private updateVoiceEditLink(editUrl?: string): void {
        if (!this.hasVoiceEditLinkTarget) {
            return
        }

        if (editUrl) {
            this.voiceEditLinkTarget.href = editUrl
            this.voiceEditLinkTarget.classList.remove('hidden')
            this.voiceEditLinkTarget.classList.add('inline-flex')
            return
        }

        this.voiceEditLinkTarget.removeAttribute('href')
        this.voiceEditLinkTarget.classList.add('hidden')
        this.voiceEditLinkTarget.classList.remove('inline-flex')
    }

    private clearVoiceOverlayHideTimer(): void {
        if (this.voiceOverlayHideTimer !== null) {
            window.clearTimeout(this.voiceOverlayHideTimer)
            this.voiceOverlayHideTimer = null
        }
    }

    private clearVoiceOverlayAnimationTimer(): void {
        if (this.voiceOverlayAnimationTimer !== null) {
            window.clearTimeout(this.voiceOverlayAnimationTimer)
            this.voiceOverlayAnimationTimer = null
        }
    }

    private isSpeechToTextSupported(): boolean {
        return typeof navigator.mediaDevices?.getUserMedia === 'function' && typeof window.MediaRecorder === 'function'
    }

    private mimeTypeToFileExtension(mimeType: string): string {
        const normalized = mimeType.toLowerCase()
        if (normalized.includes('mp4')) {
            return 'mp4'
        }
        if (normalized.includes('ogg')) {
            return 'ogg'
        }
        if (normalized.includes('wav')) {
            return 'wav'
        }
        return 'webm'
    }

    private createVoiceMealRequestId(): string {
        if (this.activeVoiceMealRequestId) {
            return this.activeVoiceMealRequestId
        }

        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID()
        }

        return `${Date.now()}-${Math.random().toString(36).slice(2)}`
    }

    private handleOutsideClick(event: Event): void {
        const target = event.target as HTMLElement
        if ((this.openValue || this.element.classList.contains('quick-actions-help-open')) && !this.element.contains(target)) {
            this.close()
            this.closeHelp()
        }
    }

    private clearLongPressTimer(): void {
        if (this.longPressTimer !== null) {
            window.clearTimeout(this.longPressTimer)
            this.longPressTimer = null
        }
    }

    private handleEscape(event: KeyboardEvent): void {
        if ((this.openValue || this.element.classList.contains('quick-actions-help-open')) && event.key === 'Escape') {
            this.close()
            this.closeHelp()
        }
    }

    private closeHelp(): void {
        this.element.classList.remove('quick-actions-help-open')
    }

    private clearSelectedState(): void {
        for (const action of this.actionTargets) {
            action.classList.remove('quick-action-item-selected')
            action.style.transitionDelay = '0ms'
        }
    }
}
